import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateChildFn,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { toObservable } from '@angular/core/rxjs-interop';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';

import { AuthStateSignalsService } from '../../services/auth-state-signals.service';
import { AccessControlService } from '../services/access-control.service';

function buildRedirectTree(router: Router, state: RouterStateSnapshot): UrlTree {
  const redirectUrl = state.url && state.url !== '/' ? state.url : null;
  return router.createUrlTree(['/sign-in-failed'], {
    queryParams: redirectUrl ? { redirectUrl } : undefined,
  });
}

interface AuthResolutionState {
  isSignedIn: boolean;
  isLoading: boolean;
  isAuthorized: boolean;
}

function waitForAuthResolution(
  authService: AuthStateSignalsService
): Observable<AuthResolutionState> {
  return combineLatest({
    isSignedIn: toObservable(authService.isSignedIn),
    isLoading: toObservable(authService.isLoading),
    isAuthorized: toObservable(authService.isAuthorized),
  }).pipe(
    filter((state) => !state.isLoading),
    take(1)
  );
}

function resolveAuthentication(
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean | UrlTree> {
  void route; // route data is currently unused but kept for future-proofing

  const authService = inject(AuthStateSignalsService);
  const accessControl = inject(AccessControlService);
  const router = inject(Router);
  const redirectTree = buildRedirectTree(router, state);

  const evaluateAuthState = () =>
    waitForAuthResolution(authService).pipe(
      map((state) => {
        if (!state.isSignedIn) {
          return redirectTree;
        }

        if (!state.isAuthorized) {
          authService.reportUnauthorizedAccess();
          return redirectTree;
        }

        if (!accessControl.canAccessTab(route.data?.['tab'])) {
          return router.createUrlTree([accessControl.preferredTabPath()]);
        }

        return true;
      })
    );

  return waitForAuthResolution(authService).pipe(
    switchMap((state) => {
      if (state.isSignedIn && state.isAuthorized) {
        if (!accessControl.canAccessTab(route.data?.['tab'])) {
          return of(router.createUrlTree([accessControl.preferredTabPath()]));
        }

        return of(true);
      }

      const autoSignIn$ = authService.attemptAutoSignIn();

      if (autoSignIn$) {
        return autoSignIn$.pipe(
          switchMap(() => evaluateAuthState()),
          catchError(() => of(redirectTree))
        );
      }

      if (state.isSignedIn && !state.isAuthorized) {
        authService.reportUnauthorizedAccess();
      }

      return of(redirectTree);
    }),
    catchError(() => of(redirectTree))
  );
}

export const calCanActivateGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => resolveAuthentication(route, state);

export const calCanActivateChildGuard: CanActivateChildFn = (
  childRoute: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => resolveAuthentication(childRoute, state);
